import getYouTubeID from "get-youtube-id";
import { observer } from "mobx-react-lite";
import { type ForwardedRef, forwardRef, useEffect, useState } from "react";
import { cn } from "ui";
import { type ExerciseThumbnail } from "utils";

import { YouTube } from "src/components/YouTube";
import { VideoThumbnail } from "src/media";

import { getExercisePreviewImage } from "./getExercisePreviewImage";

interface ExerciseValue {
  id: string;
  name: string;
  videoURL: string;
  thumbnail: ExerciseThumbnail;
}

interface Props {
  exercise: ExerciseValue;
  imageClassName?: string;
  onClick?: () => void;
  autoPlayVideoAfter?: number;
}

const baseContainerClassName =
  "w-10 h-10 rounded-[4px] overflow-hidden flex items-center justify-center bg-gray-100";
const baseImageClassName = "w-full h-full object-contain";

export const ExercisePreviewImage = observer(
  forwardRef(function ExercisePreviewImage(
    props: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const [showVideo, setShowVideo] = useState(false);
    const [isPlayingVideo, setIsPlayingVideo] = useState(false);

    const exercisePreview = getExercisePreviewImage(props.exercise);
    const youtubeId = getYouTubeID(props.exercise.videoURL);

    useEffect(() => {
      if (props.autoPlayVideoAfter && youtubeId) {
        const timer = setTimeout(() => {
          setShowVideo(true);
        }, props.autoPlayVideoAfter);

        return () => clearTimeout(timer);
      }
    }, []);

    if (!exercisePreview) {
      return (
        <div
          className={cn(
            baseContainerClassName,
            "bg-gray-200",
            props.imageClassName,
          )}
        />
      );
    }

    return (
      <div onClick={props.onClick} ref={ref}>
        {exercisePreview.type === "image" ? (
          <div className={cn(baseContainerClassName, props.imageClassName)}>
            <img
              src={exercisePreview.url}
              alt={props.exercise.name}
              className={baseImageClassName}
            />
          </div>
        ) : exercisePreview.type === "online-video-thumbnail" ? (
          <div className={cn(baseContainerClassName, props.imageClassName)}>
            {showVideo && youtubeId && (
              <div
                className={cn({
                  hidden: !isPlayingVideo,
                })}
              >
                <YouTube
                  onStateChange={(state) => {
                    setIsPlayingVideo(state.data === 1);
                  }}
                  videoId={youtubeId}
                  autoplay
                />
              </div>
            )}
            {!isPlayingVideo && (
              <VideoThumbnail
                videoURL={exercisePreview.url}
                className="w-full h-full"
                placeholder={<div className="w-full h-full bg-gray-200" />}
              />
            )}
          </div>
        ) : exercisePreview.type === "uploaded-video-thumbnail" ? (
          <div className={cn(baseContainerClassName, props.imageClassName)}>
            <video controls={false} className={baseImageClassName}>
              <source src={exercisePreview.url} />
            </video>
          </div>
        ) : null}
      </div>
    );
  }),
);
